/**
 * OperatorSelector — full-screen splash for confirming the active Stream identity.
 *
 * Resolves the signed-in Google identity to a staff profile via
 * `loadStaffProfile()` (calls `GET /v1/auth/staff/me`), then presents one of:
 *
 *   - A single profile card to confirm and sign in (happy path).
 *   - A "no account" error when the Google email has no staff record.
 *   - A "no key" error when a staff record exists but no API key is provisioned.
 *   - A retry panel for transient network / command failures.
 *
 * Once the user clicks "Sign in":
 *   1. Persists the selection via `useOperatorStore.selectOperator`.
 *   2. Triggers `useEnvironmentStore.reloadForOperator(consumer_id)` which
 *      fetches the API key from Secret Manager, reconfigures the API client,
 *      and increments `switchGeneration` to force a clean remount.
 */

import type { CSSProperties, KeyboardEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useOperatorStore } from '@/shared/stores/operator-store'
import { useEnvironmentStore } from '@/shared/stores/environment-store'
import { useTransitionStore } from '@/shared/stores/transition-store'
import { useSettingsStore } from '@/shared/stores/settings-store'
import type { Operator } from '@/lib/tauri'
import streamIcon from '@/assets/stream-icon.jpg'
import { useGoogleAuthStore } from '../../stores/google-auth-store'
import { OperatorCard } from './OperatorCard'

const APP_VERSION = (import.meta.env.MODE ?? 'unknown').toString()

/**
 * Duration of the back-button exit fade. Matches the 360ms entrance
 * transition baked into the per-element `entranceFadeStyle` and into
 * OperatorCard's opacity transition — the exit is the entrance reversed.
 *
 * Kept in sync with both:
 *   - `transition: opacity 360ms ease-out` (entranceFadeStyle below)
 *   - OperatorCard.tsx → styles.card.transition `opacity 360ms ease-out`
 */
const EXIT_DURATION_MS = 360

export function OperatorSelector() {
  const available = useOperatorStore((s) => s.available)
  const isLoaded = useOperatorStore((s) => s.isLoaded)
  const loadError = useOperatorStore((s) => s.loadError)
  const profileError = useOperatorStore((s) => s.profileError)
  const staffProfile = useOperatorStore((s) => s.staffProfile)
  const loadStaffProfile = useOperatorStore((s) => s.loadStaffProfile)
  const selectOperator = useOperatorStore((s) => s.selectOperator)
  const reloadForOperator = useEnvironmentStore((s) => s.reloadForOperator)

  const [pendingSelection, setPendingSelection] = useState<Operator | null>(
    null,
  )
  const [hoveredOperator, setHoveredOperator] = useState<Operator | null>(null)
  const [isContinuing, setIsContinuing] = useState(false)
  const [continueError, setContinueError] = useState<string | null>(null)
  const [hasMounted, setHasMounted] = useState(false)
  // Exit phase — when true, all UI elements fade out using the reverse of
  // their entrance transition. We chain the canvas unmorph behind this fade
  // so the cards are visually gone before the particles start racing toward
  // the river path. See `handleBack` below.
  const [isExiting, setIsExiting] = useState(false)
  // Gate hover-driven particle text on a deliberate mouse movement. When the
  // splash dismisses, the cursor is often resting over a card position — if
  // we wrote that hover straight to the canvas, the particles would skip
  // their orbital reveal and snap into the operator's initials. Requiring
  // an actual `mousemove` event lets the morph settle into the abstract
  // shape first; the moment the user moves their mouse (intentionally or
  // not) we flush the latest hover state through.
  const [hasMouseMoved, setHasMouseMoved] = useState(false)

  // Particle text target — hovered operator wins; otherwise selection;
  // otherwise null which lets the particle layer drift between abstract shapes.
  // We push this to the transition store so the unified canvas (mounted at
  // the App root) can read it without prop drilling.
  const particleText = useMemo(() => {
    const op = hoveredOperator ?? pendingSelection
    return op ? op.initials : null
  }, [hoveredOperator, pendingSelection])

  // First-mousemove gate. Capture-phase listener so it fires regardless of
  // which element the cursor is currently over (cards stop propagation in
  // their own handlers). One-shot — removed after the first event.
  useEffect(() => {
    if (hasMouseMoved) return
    const onMove = () => setHasMouseMoved(true)
    window.addEventListener('mousemove', onMove, { once: true })
    return () => window.removeEventListener('mousemove', onMove)
  }, [hasMouseMoved])

  // Push the latest particle text target to the canvas, but only after the
  // user has moved their mouse at least once. Re-runs when hasMouseMoved
  // flips, which retroactively flushes whatever hover state already exists.
  useEffect(() => {
    if (!hasMouseMoved) return
    useTransitionStore.getState().setParticleText(particleText)
  }, [particleText, hasMouseMoved])

  // Mount-time canvas reset.
  //
  // The OperatorSelector mounts in three distinct scenarios:
  //
  //   1. Initial app load: SplashScreen has just dismissed and triggered the
  //      river → particles morph. Phase is 'morphing' transitioning to
  //      'particles', particlesVisible is true. Nothing to do.
  //
  //   2. "Switch operator" from the kodeon-splash flow (Welcome page → here):
  //      WelcomePage's unmount cleanup just set particlesVisible to false
  //      and cleared particleText. Phase is still 'particles'. We need to
  //      flip particlesVisible back on so the canvas isn't fully transparent.
  //
  //   3. "Switch operator" from a route-landing flow (any non-Welcome page →
  //      here): the original sign-in ran a shell morph, leaving phase at
  //      'shell-revealed' which renders nothing. particlesVisible may be
  //      false (if WelcomePage was ever mounted) or true (if not). We need
  //      to both flip particlesVisible on AND reset the phase back to
  //      'particles' so the canvas resumes drawing the abstract-shape cycle.
  //
  // Without this effect, scenarios 2 and 3 leave the operator selector
  // sitting on a blank background — the cards float over nothing.
  useEffect(() => {
    const store = useTransitionStore.getState()
    store.setParticlesVisible(true)
    store.setParticleText(null)
    const phase = store.phase
    if (
      phase === 'morphing-to-shell' ||
      phase === 'shell-trace-hold' ||
      phase === 'shell-ui-revealing' ||
      phase === 'shell-particles-fading' ||
      phase === 'shell-revealed'
    ) {
      // The canvas's handlePhaseChange handler will re-prime particle
      // targets and the idle cycle (see the 'particles' branch in
      // StreamParticleCanvas) so this is a clean handoff.
      useTransitionStore.setState({ phase: 'particles' })
    }
    return () => {
      useTransitionStore.getState().setParticleText(null)
    }
  }, [])

  // Hold refs to each card button so arrow-key navigation can move focus.
  const cardRefs = useRef<Array<HTMLButtonElement | null>>([])

  useEffect(() => {
    // Trigger entrance fade after first paint. When we mount during the
    // splash → particles morph, hold the cards back for an extra second
    // after the morph completes so the user sees the full river-becomes-
    // particles animation settle into the orbital ring before any UI
    // appears over it. On subsequent mounts (e.g. "Switch operator" from
    // the sidebar), the canvas is already in steady particles mode and
    // there's nothing to watch — fade in immediately.
    const isMorphing = useTransitionStore.getState().phase === 'morphing'
    const delay = isMorphing ? 1016 : 16
    const id = window.setTimeout(() => setHasMounted(true), delay)
    return () => window.clearTimeout(id)
  }, [])

  const selectedIndex = useMemo(() => {
    if (!pendingSelection) return -1
    return available.findIndex(
      (op) => op.consumer_id === pendingSelection.consumer_id,
    )
  }, [available, pendingSelection])

  const moveSelection = useCallback(
    (delta: number) => {
      if (available.length === 0) return
      const baseIndex = selectedIndex >= 0 ? selectedIndex : 0
      const nextIndex =
        (baseIndex + delta + available.length) % available.length
      const nextOperator = available[nextIndex]
      setPendingSelection(nextOperator)
      // Move focus to the new card so keyboard users see the selection track.
      cardRefs.current[nextIndex]?.focus()
    },
    [available, selectedIndex],
  )

  const handleContinue = useCallback(async () => {
    if (!pendingSelection || isContinuing) return
    setIsContinuing(true)
    setContinueError(null)
    try {
      // Persist selection FIRST so a failed reload still leaves the operator
      // chosen — the bootstrap will surface the error and offer retry.
      selectOperator(pendingSelection)
      await reloadForOperator(pendingSelection.consumer_id)

      // Successful sign-in. The flow now branches on the operator's
      // landing-page setting:
      //
      //   - kodeon-splash (default): the AppShell renders normally with
      //     the sidebar/header visible immediately. The WelcomePage in
      //     the content area pushes 'KODEON' as particle text, and the
      //     particles (already in 'particles' phase from the splash
      //     morph) drift to spell it. We don't trigger the shell morph
      //     because we don't want particles tracing the chrome — the
      //     chrome is meant to be REAL UI from the start.
      //
      //   - specific route: trigger the shell morph so dots trace the
      //     entire destination page (sidebar, header, tables, cards)
      //     before the UI fades in behind them.
      //
      // In both cases we fade the operator-selector cards out (360ms) so
      // they don't collide with whatever appears next.
      setIsExiting(true)
      const landingPage = useSettingsStore.getState().landingPage
      if (landingPage.kind === 'route') {
        useTransitionStore.getState().triggerShellMorph(landingPage.route)
      }
      // Else: stay in 'particles' phase. The AppShell mounts via the
      // bootstrap once apiReady, WelcomePage takes over the particle text.
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setContinueError(message)
      setIsContinuing(false)
    }
  }, [pendingSelection, isContinuing, selectOperator, reloadForOperator])

  const handleCardKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, operator: Operator) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault()
          moveSelection(1)
          break
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault()
          moveSelection(-1)
          break
        case 'Home':
          e.preventDefault()
          if (available[0]) {
            setPendingSelection(available[0])
            cardRefs.current[0]?.focus()
          }
          break
        case 'End':
          e.preventDefault()
          if (available.length > 0) {
            const last = available.length - 1
            setPendingSelection(available[last])
            cardRefs.current[last]?.focus()
          }
          break
        case 'Enter':
        case ' ':
          e.preventDefault()
          if (
            pendingSelection?.consumer_id === operator.consumer_id
          ) {
            void handleContinue()
          } else {
            setPendingSelection(operator)
          }
          break
        default:
          // Single-character search (first letter of name).
          if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
            const lower = e.key.toLowerCase()
            const matchIdx = available.findIndex((op) =>
              op.display_name.toLowerCase().startsWith(lower),
            )
            if (matchIdx !== -1) {
              setPendingSelection(available[matchIdx])
              cardRefs.current[matchIdx]?.focus()
            }
          }
          break
      }
    },
    [available, moveSelection, pendingSelection, handleContinue],
  )

  // Global Enter handler — when the Continue button is focusable and a
  // selection exists, pressing Enter anywhere outside the cards activates
  // continue. Escape clears any current selection so the user can return to
  // the idle particle cycle without picking a different operator first. We
  // attach to window so neither shortcut requires the user to tab.
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (pendingSelection && !isContinuing) {
          e.preventDefault()
          setPendingSelection(null)
        }
        return
      }
      if (e.key !== 'Enter') return
      const target = e.target as HTMLElement | null
      if (target?.tagName === 'BUTTON') return // card / button itself handles it
      if (!pendingSelection || isContinuing) return
      e.preventDefault()
      void handleContinue()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [pendingSelection, isContinuing, handleContinue])

  // Re-trigger load on mount in case the bootstrap deferred it.
  // Uses `loadStaffProfile` which resolves the Google identity dynamically
  // via the backend bootstrap endpoint, replacing the hardcoded operator list.
  useEffect(() => {
    void loadStaffProfile()
  }, [loadStaffProfile])

  // Auto-select the single available profile as soon as it loads so the user
  // only has to click "Sign in" rather than pick a card.
  useEffect(() => {
    if (available.length === 1 && !pendingSelection) {
      setPendingSelection(available[0])
    }
  }, [available, pendingSelection])

  // Back button handler. Two-step transition:
  //
  //   1. Set `isExiting` so all UI elements fade their opacity to 0 using
  //      the same 360ms transition they used to fade in. This includes the
  //      cards (via `isVisible` → `hasMounted` prop), the header, the
  //      continue row, the footer, and the back button itself.
  //
  //   2. After the fade completes, trigger the canvas unmorph so the
  //      particles begin racing toward the river path. By the time the
  //      particles take flight, the cards are gone — there's no visual
  //      collision between a fading UI and flying particles.
  //
  // EXIT_DURATION_MS matches the entrance transition (360ms). We add a
  // tiny buffer (40ms) so the fade is visually complete before the
  // particles launch.
  const triggerUnmorph = useTransitionStore((s) => s.triggerUnmorph)
  const handleBack = useCallback(() => {
    if (isExiting) return
    setIsExiting(true)
    window.setTimeout(() => {
      triggerUnmorph()
    }, EXIT_DURATION_MS + 40)
  }, [isExiting, triggerUnmorph])

  // Combined visibility flag: false during the entrance fade-in (hasMounted
  // hasn't flipped yet) and during the exit fade-out (isExiting is true).
  // Both transitions use the same 360ms easing — the exit is literally the
  // entrance played in reverse.
  const isVisible = hasMounted && !isExiting

  const containerStyle: CSSProperties = {
    ...styles.container,
    opacity: isVisible ? 1 : 0,
    transition: 'opacity 360ms ease-out',
  }

  // For the main state we apply the entrance fade per-element instead of on
  // the container. The cards use `backdrop-filter` for their frosted-glass
  // look — when an ancestor has `opacity < 1` browsers render the subtree
  // to an offscreen group, which has no backdrop for the filter to read.
  // The filter is suppressed during the fade and snaps on the moment the
  // container reaches opacity 1, which the user perceives as the cards
  // "jumping to opaque". Fading each child individually keeps the cards
  // outside any opacity-bearing ancestor, so backdrop-filter works
  // consistently the whole way through.
  const entranceFadeStyle: CSSProperties = {
    opacity: isVisible ? 1 : 0,
    transition: 'opacity 360ms ease-out',
  }

  // ── Loading state ────────────────────────────────────────────────────────
  if (!isLoaded) {
    return (
      <div style={containerStyle} data-testid="operator-selector-overlay">
        <div style={styles.contentWrap}>
          <Header />
          <div style={styles.cardsRow}>
            <SkeletonCard />
          </div>
          <div style={styles.continueRow}>
            <span style={styles.statusHint}>Checking your account...</span>
          </div>
        </div>
        <GoogleAuthBadge />
        <BackButton />
        <Footer />
      </div>
    )
  }

  // ── Network / command error ───────────────────────────────────────────────
  if (loadError) {
    return (
      <div style={containerStyle} data-testid="operator-selector-overlay">
        <div style={styles.contentWrap}>
          <Header />
          <div style={styles.errorPanel} role="alert">
            <h2 style={styles.errorTitle}>Could not load your account</h2>
            <p style={styles.errorMessage}>{loadError}</p>
            <button
              type="button"
              onClick={() => void loadStaffProfile({ force: true })}
              style={styles.continueButton}
              className="button-primary"
              data-testid="operator-selector-retry-button"
            >
              Retry
            </button>
          </div>
        </div>
        <GoogleAuthBadge />
        <BackButton />
        <Footer />
      </div>
    )
  }

  // ── No account ───────────────────────────────────────────────────────────
  // Valid @kodeon.ai Google account but no staff record exists.
  if (profileError === 'no_account') {
    return (
      <div style={containerStyle} data-testid="operator-selector-overlay">
        <div style={styles.contentWrap}>
          <Header />
          <div style={styles.errorPanel} role="alert">
            <h2 style={styles.errorTitle}>No Stream account</h2>
            <p style={styles.errorMessage}>
              Your Google account ({staffProfile?.email ?? 'unknown'}) doesn&apos;t
              have a Stream account yet. Ask an admin to add you on the Staff
              page, then sign in again.
            </p>
          </div>
        </div>
        <GoogleAuthBadge />
        <BackButton />
        <Footer />
      </div>
    )
  }

  // ── No API key provisioned ────────────────────────────────────────────────
  // Staff record exists but no consumer_id — key not yet provisioned.
  if (profileError === 'no_key') {
    return (
      <div style={containerStyle} data-testid="operator-selector-overlay">
        <div style={styles.contentWrap}>
          <Header />
          <div style={styles.errorPanel} role="alert">
            <h2 style={styles.errorTitle}>Access not yet set up</h2>
            <p style={styles.errorMessage}>
              Your account exists ({staffProfile?.email ?? 'unknown'}) but your
              API key hasn&apos;t been provisioned yet. Ask an admin to complete
              your setup on the Staff page, then try again.
            </p>
            <button
              type="button"
              onClick={() => void loadStaffProfile({ force: true })}
              style={styles.continueButton}
              className="button-primary"
              data-testid="operator-selector-retry-button"
            >
              Try again
            </button>
          </div>
        </div>
        <GoogleAuthBadge />
        <BackButton />
        <Footer />
      </div>
    )
  }

  // ── Main state ───────────────────────────────────────────────────────────
  const continueDisabled = !pendingSelection || isContinuing
  const continueLabel = isContinuing ? 'Signing in...' : 'Sign in'

  return (
    <div
      style={{ ...containerStyle, opacity: 1, transition: 'none' }}
      data-testid="operator-selector-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="operator-selector-heading"
    >
      <div style={styles.contentWrap}>
        <Header fadeStyle={entranceFadeStyle} />

        <div
          style={styles.cardsRow}
          role="radiogroup"
          aria-label="Stream operators"
          onMouseLeave={() => setHoveredOperator(null)}
        >
          {available.map((op, index) => (
            <OperatorCard
              key={op.consumer_id}
              operator={op}
              hasMounted={isVisible}
              isSelected={
                pendingSelection?.consumer_id === op.consumer_id
              }
              isAnySelected={pendingSelection !== null}
              onSelect={() => {
                // Click-to-toggle: clicking the already-selected card unselects
                // it and lets the particle layer drift back into the cycling
                // abstract shapes.
                setPendingSelection((prev) =>
                  prev?.consumer_id === op.consumer_id ? null : op,
                )
              }}
              onActivate={() => {
                setPendingSelection(op)
                void handleContinue()
              }}
              onKeyDown={(e) => handleCardKeyDown(e, op)}
              innerRef={(el) => {
                cardRefs.current[index] = el
              }}
              onHoverStart={() => setHoveredOperator(op)}
              onHoverEnd={() => setHoveredOperator(null)}
            />
          ))}
        </div>

        <div style={{ ...styles.continueRow, ...entranceFadeStyle }}>
          {continueError && (
            <div style={styles.continueError} role="alert">
              {continueError}
            </div>
          )}
          <button
            type="button"
            disabled={continueDisabled}
            onClick={() => void handleContinue()}
            style={{
              ...styles.continueButton,
              opacity: continueDisabled ? 0.4 : 1,
              transform: pendingSelection ? 'scale(1)' : 'scale(0.98)',
            }}
            className="button-primary"
            data-testid="operator-selector-continue-button"
          >
            {continueLabel}
          </button>
          <span style={styles.statusHint}>
            {pendingSelection
              ? `Press Enter to sign in as ${pendingSelection.display_name}`
              : 'Confirm your account to continue'}
          </span>
        </div>
      </div>

      <GoogleAuthBadge fadeStyle={entranceFadeStyle} />
      <BackButton fadeStyle={entranceFadeStyle} onBack={handleBack} />
      <Footer fadeStyle={entranceFadeStyle} />
    </div>
  )
}

// ── Subcomponents ──────────────────────────────────────────────────────────

/**
 * GoogleAuthBadge — small top-right affordance confirming the operator is
 * signed in via Google OAuth and offering a "Sign out" action. Designed to
 * be out of the way: matches the BackButton's frosted-glass aesthetic, sits
 * in the top-right corner, and is the only ambient sign-out path in the app
 * (the documented revocation path remains Workspace Admin suspension —
 * see task 3127).
 *
 * Clicking "Sign out" calls `useGoogleAuthStore.signOut`, which:
 *   1. Invokes the `google_sign_out` Tauri command (clears the Keychain
 *      refresh token).
 *   2. Sets store status to `'unauthenticated'`, which causes the App.tsx
 *      render switch to unmount this OperatorSelector and mount the
 *      `SignInGate` overlay.
 */
function GoogleAuthBadge({ fadeStyle }: { fadeStyle?: CSSProperties }) {
  const email = useGoogleAuthStore((s) => s.email)
  const status = useGoogleAuthStore((s) => s.status)
  const signOut = useGoogleAuthStore((s) => s.signOut)

  // Only render when we have a confirmed Google auth — the badge is meant
  // to confirm a state, not advertise the gate while it's loading.
  if (status !== 'authenticated') return null

  return (
    <div style={{ ...styles.authBadge, ...fadeStyle }} data-testid="google-auth-badge">
      <div style={styles.authBadgeRow}>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
          style={styles.authBadgeCheck}
        >
          <path
            d="M2.5 6.25L4.75 8.5L9.5 3.75"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span style={styles.authBadgeLabel}>Kodeon OAuth</span>
      </div>
      {email && <p style={styles.authBadgeEmail} title={email}>{email}</p>}
      <button
        type="button"
        onClick={() => void signOut()}
        style={styles.signOutButton}
        data-testid="google-auth-badge-sign-out"
        aria-label="Sign out of Google"
      >
        Sign out
      </button>
    </div>
  )
}


/**
 * `fadeStyle` lets the main-state caller drive the entrance opacity per-element
 * instead of via a parent container fade. Loading/error states omit it and use
 * the parent containerStyle fade. See the comment in OperatorSelector around
 * `entranceFadeStyle` for the rationale (parent opacity breaks card
 * backdrop-filter compositing during the fade).
 */
function Header({ fadeStyle }: { fadeStyle?: CSSProperties }) {
  return (
    <header style={{ ...styles.header, ...fadeStyle }}>
      <div style={styles.logoWrap}>
        <img src={streamIcon} alt="" style={styles.logo} />
      </div>
      <p style={styles.eyebrow}>Kodeon Stream</p>
      <h1 id="operator-selector-heading" style={styles.heading}>
        Log in
      </h1>
    </header>
  )
}

/**
 * Back button — positioned at the lower-left corner of the screen.
 *
 * Click is delegated to the parent's `onBack` handler so the parent can
 * sequence the UI exit fade before the canvas unmorph fires. Falls back to
 * triggering the unmorph directly when no handler is supplied (used by
 * loading / error states where there's no exit fade to coordinate).
 */
function BackButton({
  fadeStyle,
  onBack,
}: {
  fadeStyle?: CSSProperties
  onBack?: () => void
}) {
  const triggerUnmorph = useTransitionStore((s) => s.triggerUnmorph)

  const handleClick = () => {
    if (onBack) {
      onBack()
    } else {
      triggerUnmorph()
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{ ...styles.backButton, ...fadeStyle }}
      className="button-ghost"
      aria-label="Back to splash screen"
      data-testid="operator-selector-back-button"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={styles.backIcon}
      >
        <path
          d="M12.5 15L7.5 10L12.5 5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Back
    </button>
  )
}

function Footer({ fadeStyle }: { fadeStyle?: CSSProperties }) {
  return (
    <footer style={{ ...styles.footer, ...fadeStyle }}>
      <span>Stream</span>
      <span style={styles.footerSeparator} aria-hidden>
        ·
      </span>
      <span>Build {APP_VERSION}</span>
    </footer>
  )
}


function SkeletonCard() {
  return (
    <div style={styles.skeletonCard} aria-hidden>
      <div style={styles.skeletonAvatar} className="skeleton" />
      <div style={styles.skeletonName} className="skeleton" />
      <div style={styles.skeletonId} className="skeleton" />
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    zIndex: 9000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    // Transparent — the unified canvas at the App root provides the animated
    // background. The body element supplies bg-secondary as a fallback.
    background: 'transparent',
    overflow: 'hidden',
  },
  contentWrap: {
    // Layout-only wrapper — sits over the particle background with no
    // surrounding glass panel. Keeps the header, cards row, and continue
    // controls vertically stacked and centred.
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '40px',
    maxWidth: '1000px',
  },
  header: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    textAlign: 'center',
  },
  backButton: {
    position: 'absolute',
    bottom: '24px',
    left: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    // Theme-aware tokens, NOT the `tokens.*` module — that exports static
    // dark-mode hexes and is not theme-reactive. A white-alpha overlay only
    // reads as a surface over a dark ground; over the light ground this
    // container is transparent against, the button lost its fill and edge.
    background: 'var(--surface-elevated-strong)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
    backdropFilter: 'blur(8px)',
    zIndex: 2,
  },
  authBadge: {
    position: 'absolute',
    top: '24px',
    right: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
    padding: '8px 12px',
    background: 'var(--surface-elevated-soft)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-md)',
    backdropFilter: 'blur(8px)',
    maxWidth: '240px',
    zIndex: 2,
    transition: 'opacity 360ms ease-out',
  },
  authBadgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: 'var(--text-secondary)',
  },
  authBadgeCheck: {
    color: '#34d399', // emerald-400 — a quiet "verified" green, not loud
    flexShrink: 0,
  },
  authBadgeLabel: {
    whiteSpace: 'nowrap',
  },
  authBadgeEmail: {
    margin: 0,
    fontSize: '10px',
    color: 'var(--text-muted)',
    fontFamily: 'JetBrains Mono, monospace',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '220px',
  },
  signOutButton: {
    marginTop: '2px',
    padding: '2px 0',
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--text-muted)',
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid transparent',
    cursor: 'pointer',
    transition: 'color var(--transition-fast), border-color var(--transition-fast)',
  },
  backIcon: {
    width: '20px',
    height: '20px',
  },
  logoWrap: {
    display: 'flex',
    width: '72px',
    height: '72px',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '12px',
    borderRadius: '18px',
    // The accent ring is hue-neutral, so the accent tokens hold in both
    // themes; --logo-ring-shadow carries the glow and drops the white bevel
    // under light. Same treatment as SignInGate's ring, which this duplicates.
    border: '1px solid var(--accent-border-strong)',
    background: 'var(--accent-bg-medium)',
    boxShadow: 'var(--logo-ring-shadow)',
    overflow: 'hidden',
  },
  logo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  eyebrow: {
    margin: 0,
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: 'var(--text-muted)',
  },
  heading: {
    margin: 0,
    fontSize: '42px',
    fontWeight: 700,
    letterSpacing: '-0.03em',
    color: 'var(--text-primary)',
    // A black halo lifts near-white text off the animated canvas in dark mode;
    // under light it is a smudge around near-black text, so the token resolves
    // to `none` there. Safe as a text-shadow (sole value), unlike a box-shadow.
    textShadow: 'var(--heading-text-shadow)',
  },
  cardsRow: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    gap: '20px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    maxWidth: '900px',
  },
  continueRow: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  continueButton: {
    minWidth: '180px',
    padding: '14px 32px',
    fontSize: '15px',
    fontWeight: 600,
    color: 'white',
    borderRadius: 'var(--radius-md)',
    // Deliberately a literal, matching SignInGate's primary button: the glow is
    // the accent blue, which is the same hex in both themes, so it reads
    // correctly on light and dark alike. Left in place rather than tokenised so
    // the two primary buttons stay identical.
    boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
    transition:
      'all var(--transition-fast), transform 200ms cubic-bezier(0.16, 1, 0.3, 1)',
  },
  continueError: {
    maxWidth: '480px',
    padding: '8px 12px',
    fontSize: '12px',
    color: 'var(--error)',
    background: 'var(--error-bg-medium)',
    border: '1px solid var(--error-border-medium)',
    borderRadius: 'var(--radius-sm)',
    textAlign: 'center',
  },
  statusHint: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    minHeight: '16px',
  },
  errorPanel: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    maxWidth: '520px',
    padding: '24px',
    background: 'var(--error-bg-medium)',
    border: '1px solid var(--error-border-medium)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
  },
  errorTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: 'var(--error)',
  },
  errorMessage: {
    margin: 0,
    fontSize: '13px',
    lineHeight: 1.5,
    color: 'var(--text-secondary)',
    fontFamily: 'JetBrains Mono, monospace',
    textAlign: 'center',
    wordBreak: 'break-word',
  },
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    fontSize: '11px',
    color: 'var(--text-faint)',
    letterSpacing: '0.04em',
  },
  footerSeparator: {
    color: 'var(--text-faint)',
  },
  skeletonCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
    width: '180px',
    height: '220px',
    padding: '24px 16px 20px',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--surface-card-soft)',
    backdropFilter: 'blur(8px)',
  },
  skeletonAvatar: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
  },
  skeletonName: {
    width: '120px',
    height: '14px',
  },
  skeletonId: {
    width: '90px',
    height: '10px',
    opacity: 0.6,
  },
}
